import { useQuery } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { fetchMyCompanies, type Company } from "./companies";
import { useParticipation } from "./useParticipation";

/**
 * LES SOCIÉTÉS DU PARTICIPANT, ET CELLE QU'IL REPRÉSENTE ICI.
 *
 * Deux lecteurs, donc un seul endroit : l'étape société (qui les liste et en change)
 * et le récapitulatif de la confirmation (qui nomme la retenue). Écrite deux fois, la
 * règle qui désigne « celle-ci » aurait dérivé — et le récapitulatif est justement
 * l'écran où une divergence se lit comme un mensonge.
 *
 * ⚠️ LA RETENUE SE RECONNAÎT AU RATTACHEMENT, PAS À LA SOCIÉTÉ. `UserEvents.employeeId`
 * désigne une ligne `Employees`, c'est-à-dire un couple personne–société : quelqu'un
 * employé de deux maisons du domaine a deux rattachements, et un seul vaut pour cet
 * événement. Comparer des identifiants de SOCIÉTÉ ne saurait pas les départager.
 */
export function useCompany(
  /**
   * Faut-il seulement demander ? La confirmation ne le fait que si l'étape existe :
   * une requête par confirmation, sur tous les événements qui n'ont pas de sociétés,
   * pour une ligne qui ne s'afficherait jamais.
   */
  active: boolean,
): {
  companies: ReadonlyArray<Company>;
  attached: Company | undefined;
  /** La liste n'a pas pu être lue — dit, plutôt que rendu en « aucune société ». */
  failure: boolean;
  /** La question a eu sa réponse, succès ou échec : `attached` ne changera plus. */
  fetched: boolean;
} {
  const event = useEvent();
  const { me, recorded } = useParticipation();

  const q = useQuery({
    queryKey: ["companies", event?.domainId, me?.id],
    queryFn: ({ signal }) =>
      fetchMyCompanies(event!.domainId, me!.id, signal),
    enabled: active && !!event && typeof me?.id === "number",
    staleTime: Infinity,
    retry: false,
  });

  const companies = q.data ?? [];
  return {
    companies,
    attached: companies.find((c) => c.employeeId === recorded?.employeeId),
    failure: q.isError,
    fetched: q.isFetched,
  };
}
